import { createContext, ReactNode, useContext, useMemo, useCallback } from "react";
import { Subscription, SUBSCRIPTION_STATUS, SUBSCRIPTION_TYPE } from "../utils/subscriptionTypes";
import { AxiosError } from "axios";
import { getRequest } from "../api/apiServices";
import apiList from "../api/apiList";
import { isHiringManagerUser } from "../utils/helper";
import { useQuery } from '@tanstack/react-query';

interface SubscriptionContextType {
    subscription: Subscription | null;
    isLoading: boolean;
    error: AxiosError | null;
    refetch: () => void;
    hasActiveSubscription: boolean;
    canAccessFeature: (requiredPlans?: SUBSCRIPTION_TYPE[]) => boolean;
    // New method to trigger subscription check
    checkSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const useMockData = false;

const defaultSubscription = (): Subscription => ({
    type: SUBSCRIPTION_TYPE.FREE,
    status: SUBSCRIPTION_STATUS.EXPIRED,
    startDate: '',
    endDate: '',
    isPremium: false,
});

const fetchSubscription = async (): Promise<Subscription> => {
    if (useMockData) {
        return Promise.resolve({
            type: SUBSCRIPTION_TYPE.FREE,
            status: SUBSCRIPTION_STATUS.ACTIVE,
            startDate: '2025-07-29T08:18:16.000Z',
            endDate: '2025-08-01T00:00:00.000Z',
            isPremium: false
        });
    }

    if (isHiringManagerUser()) {
        return defaultSubscription();
    }
    
    try {
        const response: any = await getRequest(apiList.purchaseSubscriptionStatus);
        
        if (!response) {
            return defaultSubscription();
        }

        const apiResponse = response.data;

        if (apiResponse.success === false) {
            return defaultSubscription();
        }

        if (apiResponse.success === true && apiResponse.data) {
            const subscriptionData = apiResponse.data;
            const processedSubscription: Subscription = {
                type: subscriptionData.type || SUBSCRIPTION_TYPE.FREE,
                status: subscriptionData.status || SUBSCRIPTION_STATUS.EXPIRED,
                startDate: subscriptionData.startDate || '',
                endDate: subscriptionData.endDate || '',
                isPremium: subscriptionData.isPremium || false
            };
            
            return processedSubscription;
        }

        return defaultSubscription();
    } catch {
        return defaultSubscription();
    }
}

// Updated provider that actually manages subscription state
export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
    const { data, isLoading, error, refetch } = useQuery<Subscription, AxiosError, Subscription>({
        queryKey: ['subscription'],
        queryFn: fetchSubscription,
        staleTime: 0, // Always consider data stale tselectedCampaignso ensure fresh checks
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't auto-fetch on mount
        enabled: false, // Start disabled, will be enabled when needed
    });

    const hasActiveSubscription = useMemo(() => {
        return data?.status === SUBSCRIPTION_STATUS.ACTIVE;
    }, [data?.status]);

    const canAccessFeature = useMemo(() => {
        return (requiredPlans?: SUBSCRIPTION_TYPE[]) => {
            if (!data || !hasActiveSubscription) return false;
            if (!requiredPlans || requiredPlans.length === 0) return true;
            return requiredPlans.includes(data.type);
        };
    }, [data, hasActiveSubscription]);

    // Method to trigger subscription check
    const checkSubscription = useCallback(() => {
        refetch();
    }, [refetch]);

    const contextValue: SubscriptionContextType = {
        subscription: data || null,
        isLoading,
        error,
        refetch,
        hasActiveSubscription,
        canAccessFeature,
        checkSubscription
    };

    return (
        <SubscriptionContext.Provider value={contextValue}>
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = (): SubscriptionContextType => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

// Keep the conditional hook for backward compatibility if needed elsewhere
export const useConditionalSubscription = (shouldFetch: boolean = false) => {
    const { data, isLoading, error, refetch } = useQuery<Subscription, AxiosError, Subscription>({
        queryKey: ['subscription-conditional'],
        queryFn: fetchSubscription,
        staleTime: 0,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        enabled: shouldFetch
    });

    const hasActiveSubscription = useMemo(() => {
        return data?.status === SUBSCRIPTION_STATUS.ACTIVE;
    }, [data?.status]);

    const canAccessFeature = useMemo(() => {
        return (requiredPlans?: SUBSCRIPTION_TYPE[]) => {
            if (!data || !hasActiveSubscription) return false;
            if (!requiredPlans || requiredPlans.length === 0) return true;
            return requiredPlans.includes(data.type);
        };
    }, [data, hasActiveSubscription]);

    return {
        subscription: data || null,
        isLoading,
        error,
        refetch,
        hasActiveSubscription,
        canAccessFeature
    };
};